'use client';

import React, { useState, type ChangeEvent } from 'react';
import { motion, Variants } from 'framer-motion';
import { useRouter } from 'next/navigation';
import { 
  Button,
  Card, 
  CardHeader, 
  CardTitle, 
  CardDescription, 
  CardContent,
  CardFooter,
  Input,
  FormField,
  toast,
} from '../../../components/ui/design-system';
import { AuthService } from '@repo/shared';
import Link from 'next/link';
import { supabaseAdapter } from '../../../adapters/supabase';

// Initialize auth service with web adapter
AuthService.initialize(supabaseAdapter);

// Animation variants
const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5 } }
};

const slideUp: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

export default function LoginPage(): React.ReactElement {
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value);
  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value);
  const handleRememberMeChange = (e: ChangeEvent<HTMLInputElement>) => setRememberMe(e.target.checked);

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Show loading toast
    const loadingToastId = toast('Signing in...', {
      duration: 5000,
    });

    try {
      const result = await AuthService.signIn(email, password);
      if (result.error) {
        throw new Error(result.error);
      }
      
      // Dismiss loading toast
      toast.dismiss(loadingToastId);
      
      // Show success toast
      toast.success('Signed in successfully!', {
        description: 'Redirecting to dashboard...',
      });
      
      router.push('/dashboard');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to sign in';
      setError(errorMessage);
      setLoading(false);
      
      // Dismiss loading toast and show error toast
      toast.dismiss(loadingToastId);
      toast.error('Authentication failed', {
        description: errorMessage,
      });
    }
  };

  const handleGoogleLogin = async () => {
    try {
      // Show loading toast for Google sign-in
      toast('Connecting to Google...', {
        duration: 5000,
      });
      
      await AuthService.signInWithProvider('google');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to sign in with Google';
      setError(errorMessage);
      
      // Show error toast
      toast.error('Google sign-in failed', {
        description: errorMessage,
      });
    }
  };

  return (
    <div className="max-w-md mx-auto px-4">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        className="text-center mb-8"
      >
        <h1 className="text-3xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-red-500 to-purple-500 dark:from-red-400 dark:to-purple-400">
          Welcome Back
        </h1>
        <div className="w-16 h-1 mx-auto my-4 bg-gradient-to-r from-red-500/50 to-purple-500/50 premium-premium-radius-base-full"></div>
        <p className="text-gray-600 dark:text-gray-300">
          Sign in to continue to your account
        </p>
      </motion.div>

      <motion.div
        initial="hidden"
        animate="visible"
        variants={slideUp}
      >
        <Card className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border border-gray-100 dark:border-gray-700/50 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
          <CardContent className="p-6 space-y-6">
            <form onSubmit={handleLogin} className="space-y-6">
              <FormField label="Email" required>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder="Email address"
                  disabled={loading}
                  className="shadow-sm"
                />
              </FormField>

              <FormField label="Password" required>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="Password"
                  disabled={loading}
                  className="shadow-sm"
                />
              </FormField>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={handleRememberMeChange}
                    className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 premium-premium-radius-base"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                    Remember me
                  </label>
                </div>
                <Link
                  href="/auth/forgot-password"
                  className="text-sm font-medium text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300"
                >
                  Forgot password?
                </Link>
              </div>

              {error && (
                <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 premium-premium-radius-base-md">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                variant="primary"
                className="w-full shadow-md hover:shadow-lg"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                  Or continue with
                </span>
              </div>
            </div>

            <Button
              onClick={handleGoogleLogin}
              variant="secondary"
              className="w-full shadow-sm hover:shadow-md"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"
                />
              </svg>
              Continue with Google
            </Button>

            <div className="text-center space-y-2">
              <p className="text-sm">
                Don&apos;t have an account?{' '}
                <Link href="/auth/signup" className="font-medium text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300">
                  Sign up
                </Link>
              </p>
            </div>

            <p className="text-xs text-center text-gray-500 dark:text-gray-400">
              By signing in, you agree to our{' '}
              <Link href="/terms" className="text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link href="/privacy" className="text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300">
                Privacy Policy
              </Link>
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
} 